import { useMutation } from '@apollo/client';
import { useInstallationContext } from '../../../../../context/installation';
import {
  LoggingInfo,
  LogLevel,
  PushBluetoothLogMutation,
  PushBluetoothLogMutationVariables,
} from '../../../../../data-access/gql-types/graphql';
import { PUSH_BLUETOOTH_LOG } from '../../../../../data-access/mutations/logs';
import { PushBTLogPayload, ShowAndSendMethodPayload } from '../types';
import { logMessage } from '../utils';

export const useBTLogs = () => {
  const { selectedInstallationId } = useInstallationContext();
  const [pushBluetoothLog] = useMutation<PushBluetoothLogMutation, PushBluetoothLogMutationVariables>(
    PUSH_BLUETOOTH_LOG,
  );

  const pushBTLog = ({ macAddress, loggingInfo, messages, sessionData }: PushBTLogPayload) => {
    pushBluetoothLog({
      variables: {
        input: {
          installationId: selectedInstallationId,
          macAddress,
          messages: [messages],
          loggingInfo,
          ...(sessionData?.sessionId && { sessionId: sessionData.sessionId }),
          ...(sessionData?.validFrom && { validFrom: sessionData.validFrom }),
          ...(sessionData?.validTo && { validTo: sessionData.validTo }),
        },
      },
    });
  };

  const showAndSendLog = ({ btLogLevel, loggingInfo, messages, macAddress, sessionData }: ShowAndSendMethodPayload) => {
    if (btLogLevel === LogLevel.None || !macAddress) return;

    switch (loggingInfo) {
      case LoggingInfo.None:
        logMessage(`LOG > ${messages.join(' ')}`, btLogLevel);

        pushBTLog({
          macAddress,
          loggingInfo: LoggingInfo.None,
          messages: messages.map((message) => parseInt(message, 16)),
          sessionData,
        });

        break;
      case LoggingInfo.Connected:
        logMessage(`LOG > Connected to device MAC: ${macAddress}`, btLogLevel);

        pushBTLog({
          macAddress,
          loggingInfo: LoggingInfo.Connected,
          messages: [],
          sessionData,
        });

        break;
      case LoggingInfo.Disconnected:
        logMessage(`LOG > Disconnected device MAC: ${macAddress}`, btLogLevel);

        pushBTLog({
          macAddress,
          loggingInfo: LoggingInfo.Disconnected,
          messages: [],
          sessionData,
        });

        break;
      case LoggingInfo.Supported:
        logMessage(`LOG > Logs supported for device MAC: ${macAddress}`, btLogLevel);

        pushBTLog({
          macAddress,
          loggingInfo: LoggingInfo.Supported,
          messages: [],
          sessionData,
        });

        break;
      case LoggingInfo.NotSupported:
        logMessage(`LOG > Logs not supported for device MAC: ${macAddress}`, btLogLevel);

        pushBTLog({
          macAddress,
          loggingInfo: LoggingInfo.NotSupported,
          messages: [],
          sessionData,
        });

        break;
      default:
        break;
    }
  };

  return {
    showAndSendLog,
  };
};
